import { supabaseFetch } from './core';
import { getAthleteXP } from './xp';
import { evaluateNewBadges } from '../badgeDefinitions';

/**
 * Fetch all badge keys earned by the user, with earned_at timestamps.
 * @returns {Array<{ badge_key, earned_at }>}
 */
export const getAthleteBadges = async (userId) => {
  const data = await supabaseFetch(`/athlete_badges?user_id=eq.${userId}&select=badge_key,earned_at&order=earned_at.asc`);
  return Array.isArray(data) ? data : [];
};

/**
 * Award a badge. Idempotent via UNIQUE(user_id, badge_key).
 * @returns {boolean} true se il badge è stato inserito ORA (con
 * ignore-duplicates il duplicato risponde 2xx con representation VUOTA,
 * non con errore: il flag va letto dalla risposta — stesso pattern degli XP).
 */
export const awardBadge = async (userId, badgeKey) => {
  try {
    const inserted = await supabaseFetch('/athlete_badges', {
      method: 'POST',
      headers: { 'Prefer': 'return=representation,resolution=ignore-duplicates' },
      body: JSON.stringify({ user_id: userId, badge_key: badgeKey }),
    });
    return Array.isArray(inserted) && inserted.length > 0;
  } catch (err) {
    const msg = err.message || '';
    if (!msg.includes('23505') && !msg.includes('duplicate') && !msg.includes('unique')) {
      throw err;
    }
    return false;
  }
};

/**
 * Valuta e assegna i badge subito dopo un allenamento; ritorna SOLO quelli
 * inseriti in questo momento (in un retry/doppio completamento risultano già
 * assegnati → array vuoto → nessuna doppia notifica). Da chiamare DOPO
 * l'assegnazione XP, così i badge di livello vedono i totali aggiornati.
 * @returns {Array<{key,name,icon,description,rarity,secret}>}
 */
export const evaluateBadgesAfterWorkout = async (userId) => {
  const [stats, earned] = await Promise.all([
    getGamificationStats(userId),
    getAthleteBadges(userId),
  ]);
  const candidates = evaluateNewBadges(stats, earned.map((b) => b.badge_key));
  const newlyAwarded = [];
  for (const badge of candidates) {
    const insertedNow = await awardBadge(userId, badge.key);
    if (insertedNow) {
      newlyAwarded.push({
        key: badge.key,
        name: badge.name,
        icon: badge.icon,
        description: badge.description,
        rarity: badge.rarity,
        secret: !!badge.secret,
      });
    }
  }
  return newlyAwarded;
};

/**
 * Set the selected title/badge displayed on the athlete's profile.
 * Pass null to clear the selection.
 */
export const setSelectedBadge = async (userId, badgeKey) => {
  await supabaseFetch(`/athlete_xp?user_id=eq.${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ selected_badge: badgeKey }),
  });
};

/**
 * Fetch all gamification stats needed to evaluate badges and objectives.
 */
export const getGamificationStats = async (userId) => {
  const [xpData, workoutLogs] = await Promise.all([
    getAthleteXP(userId),
    supabaseFetch(`/workout_logs?athlete_id=eq.${userId}&select=id,workout_id`),
  ]);

  const logs = Array.isArray(workoutLogs) ? workoutLogs : [];
  const totalWorkouts = logs.length;
  const freeWorkouts = logs.filter((l) => l.workout_id === null).length;

  let extraSets = 0;
  if (logs.length > 0) {
    const ids = logs.map((l) => l.id).join(',');
    try {
      const extraLogs = await supabaseFetch(
        `/exercise_logs?workout_log_id=in.(${ids})&is_extra=eq.true&select=id`
      );
      extraSets = Array.isArray(extraLogs) ? extraLogs.length : 0;
    } catch (_) {
      // non-critical
    }
  }

  return {
    totalXp: xpData?.total_xp || 0,
    currentLevel: xpData?.current_level || 1,
    selectedBadge: xpData?.selected_badge || null,
    totalWorkouts,
    freeWorkouts,
    extraSets,
  };
};

/**
 * Get recent XP events (last N).
 */
export const getRecentXpEvents = async (userId, limit = 8) => {
  const data = await supabaseFetch(
    `/xp_events?user_id=eq.${userId}&select=xp_amount,event_type,created_at&order=created_at.desc&limit=${limit}`
  );
  return Array.isArray(data) ? data : [];
};
